import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import DropUpload from "@/components/DropUpload";
import RuleBuilder from "@/components/RuleBuilder";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError, todayISO } from "@/lib/format";
import {
  Compass,
  Users,
  MapPin,
  DollarSign,
  Film,
  Sparkles,
  ClipboardCheck,
  Rocket,
  ArrowRight,
  ArrowLeft,
  Check,
  Cloud,
  Timer,
  Zap,
} from "lucide-react";

/**
 * /campaigns/new — 8-step Mirrorloops campaign builder.
 *
 * 01 Objective  · 02 Audience  · 03 Locations  · 04 Budget
 * 05 Creative   · 06 Programmability  · 07 Review  · 08 Launch
 *
 * Right rail = Intelligence panel — reasoning + suggestions per step.
 */

const OBJECTIVES = [
  { key: "awareness", label: "Awareness", desc: "Maximum eyeballs on your message.", icon: Compass, accent: "#00E5FF" },
  { key: "consideration", label: "Consideration", desc: "Get people to look up your brand or product.", icon: Film, accent: "#E8D6A3" },
  { key: "action", label: "Store visits", desc: "Drive footfall to a physical location.", icon: MapPin, accent: "#CCFF00" },
  { key: "launch", label: "Launch moment", desc: "Concentrated push around a specific date/event.", icon: Rocket, accent: "#a78bfa" },
];

const ADVERTISER_TYPES = [
  { key: "artist", label: "Artist", desc: "musician · film · brand · creator", accent: "#00E5FF" },
  { key: "business", label: "Business", desc: "brand · retail · agency", accent: "#E8D6A3" },
  { key: "individual", label: "Person", desc: "cause · community · local", accent: "#CCFF00" },
];

const CITIES = [
  { id: "nyc", name: "New York", cpm: 42, daily: 18000 },
  { id: "lon", name: "London", cpm: 38, daily: 14500 },
  { id: "tok", name: "Tokyo", cpm: 35, daily: 16200 },
  { id: "sin", name: "Singapore", cpm: 32, daily: 9800 },
  { id: "dxb", name: "Dubai", cpm: 30, daily: 11200 },
  { id: "blr", name: "Bengaluru", cpm: 12, daily: 22000 },
  { id: "mum", name: "Mumbai", cpm: 15, daily: 26000 },
  { id: "del", name: "Delhi", cpm: 14, daily: 24000 },
];

const STEPS = [
  { key: "objective", num: "01", label: "Objective", icon: Compass },
  { key: "audience", num: "02", label: "Audience", icon: Users },
  { key: "locations", num: "03", label: "Locations", icon: MapPin },
  { key: "budget", num: "04", label: "Budget", icon: DollarSign },
  { key: "creative", num: "05", label: "Creative", icon: Film },
  { key: "programmability", num: "06", label: "Programmable", icon: Sparkles },
  { key: "review", num: "07", label: "Review", icon: ClipboardCheck },
  { key: "launch", num: "08", label: "Launch", icon: Rocket },
];

export default function CampaignBuilder() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    objective: "awareness",
    advertiser_type: "business",
    advertiser_company: "",
    cities: ["blr", "mum"],
    start_date: todayISO(),
    end_date: "2026-12-31",
    total_budget: 5000,
    creative_url: "",
    creative_type: "image",
    click_url: "",
    rules: [],
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const selectedCities = useMemo(
    () => CITIES.filter((c) => form.cities.includes(c.id)),
    [form.cities]
  );
  const avgCpm = selectedCities.length
    ? selectedCities.reduce((a, c) => a + c.cpm, 0) / selectedCities.length
    : 0;
  const dailyAudience = selectedCities.reduce((a, c) => a + c.daily, 0);
  const estDailyImpressions = Math.round(dailyAudience * 0.4);
  const monthlySold = Math.round(estDailyImpressions * 30 * 0.55);

  const gate = () => {
    if (step === 0 && !form.name) return "Campaign name is required.";
    if (step === 2 && !form.cities.length) return "Pick at least one city.";
    if (step === 3 && form.total_budget < 500) return "Minimum budget is 500.";
    if (step === 4 && !form.creative_url) return "Upload a creative to continue.";
    return null;
  };

  const next = () => {
    const err = gate();
    if (err) return toast.error(err);
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const back = () => step > 0 && setStep(step - 1);

  const launch = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/ads/campaigns", {
        name: form.name,
        creative_url: form.creative_url,
        creative_type: form.creative_type,
        click_url: form.click_url,
        advertiser_company: form.advertiser_company,
        advertiser_type: form.advertiser_type,
        targeted_hub_ids: [], // MVP: hub-based targeting still lives in AdsDashboard
        start_date: form.start_date,
        end_date: form.end_date,
        total_budget: Number(form.total_budget),
        rules: form.rules,
      });
      // then submit for review
      await api.post(`/ads/campaigns/${data.id}/submit`);
      toast.success("Campaign submitted for review · Mirrorloops HQ will approve within 24h");
      nav("/app/ads");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const s = STEPS[step].key;

  return (
    <div className="space-y-8" data-testid="campaign-builder">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StateBadge state="BETA" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              8-step campaign builder
            </span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
            Launch a campaign.
          </h1>
        </div>
        <Link to="/app/ads" className="text-sm text-white/50 hover:text-white">
          Cancel & return to Ads →
        </Link>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap gap-1" data-testid="campaign-stepper">
        {STEPS.map((st, i) => {
          const Icon = st.icon;
          const done = i < step;
          const active = i === step;
          return (
            <button
              key={st.key}
              onClick={() => (i < step ? setStep(i) : null)}
              data-testid={`campaign-step-${st.key}`}
              className={`flex items-center gap-2 px-3 py-2 text-xs border transition ${
                active
                  ? "border-[#E8D6A3] text-[#E8D6A3] bg-[#D4B878]/10"
                  : done
                  ? "border-emerald-500/40 text-emerald-400"
                  : "border-white/10 text-white/40"
              }`}
            >
              {done ? <Check size={12} /> : <Icon size={12} />}
              <span className="font-mono">{st.num}</span>
              <span>{st.label}</span>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
        {/* MAIN */}
        <div className="border border-white/10 bg-white/[0.02] p-8 min-h-[500px]" data-testid={`campaign-panel-${s}`}>
          {s === "objective" && (
            <div className="space-y-6">
              <SectionHead num="01" title="Objective" desc="Why does this campaign exist?" />
              <div>
                <Label>Campaign name</Label>
                <Input
                  data-testid="cb-name"
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="e.g. Spring 2026 · Artist EP"
                  className="mt-2 bg-black border-white/10"
                />
              </div>
              <div>
                <Label>Objective</Label>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {OBJECTIVES.map((o) => {
                    const on = form.objective === o.key;
                    const I = o.icon;
                    return (
                      <button
                        key={o.key}
                        onClick={() => set({ objective: o.key })}
                        data-testid={`cb-objective-${o.key}`}
                        className={`text-left p-4 border transition ${
                          on ? "border-[#E8D6A3] bg-[#D4B878]/10" : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        <I size={16} style={{ color: o.accent }} className="mb-2" />
                        <div className="font-bold text-sm">{o.label}</div>
                        <div className="text-[11px] text-white/50 mt-1">{o.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {s === "audience" && (
            <div className="space-y-6">
              <SectionHead num="02" title="Audience" desc="Who is buying this campaign?" />
              <div>
                <Label>I am an…</Label>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {ADVERTISER_TYPES.map((t) => {
                    const on = form.advertiser_type === t.key;
                    return (
                      <button
                        key={t.key}
                        onClick={() => set({ advertiser_type: t.key })}
                        data-testid={`cb-advertiser-${t.key}`}
                        className={`text-left p-4 border transition ${
                          on ? "border-[#E8D6A3] bg-[#D4B878]/10" : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        <div className="text-sm font-bold" style={{ color: on ? t.accent : "white" }}>
                          {t.label}
                        </div>
                        <div className="text-[10px] text-white/50 mt-1">{t.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Brand / project name (optional)</Label>
                <Input
                  data-testid="cb-company"
                  value={form.advertiser_company}
                  onChange={(e) => set({ advertiser_company: e.target.value })}
                  placeholder="e.g. Kaya Records"
                  className="mt-2 bg-black border-white/10"
                />
              </div>
            </div>
          )}

          {s === "locations" && (
            <div className="space-y-6">
              <SectionHead num="03" title="Locations" desc="Where should this run?" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="cb-cities">
                {CITIES.map((c) => {
                  const on = form.cities.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() =>
                        set({
                          cities: on
                            ? form.cities.filter((x) => x !== c.id)
                            : [...form.cities, c.id],
                        })
                      }
                      data-testid={`cb-city-${c.id}`}
                      className={`text-left p-3 border transition ${
                        on ? "border-[#00E5FF] bg-[#00E5FF]/5" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <div className="font-display font-extrabold">{c.name}</div>
                      <div className="font-mono text-[10px] text-white/50 mt-1">
                        CPM ${c.cpm} · {c.daily.toLocaleString()} / day
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {s === "budget" && (
            <div className="space-y-6">
              <SectionHead num="04" title="Budget" desc="How much are you spending?" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => set({ start_date: e.target.value })}
                    data-testid="cb-start"
                    className="mt-2 bg-black border-white/10"
                  />
                </div>
                <div>
                  <Label>End</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => set({ end_date: e.target.value })}
                    data-testid="cb-end"
                    className="mt-2 bg-black border-white/10"
                  />
                </div>
              </div>
              <div>
                <Label>Total budget</Label>
                <input
                  type="range"
                  min={500}
                  max={100000}
                  step={500}
                  value={form.total_budget}
                  onChange={(e) => set({ total_budget: +e.target.value })}
                  data-testid="cb-budget"
                  className="w-full accent-[#E8D6A3] mt-3"
                />
                <div className="flex items-center justify-between mt-2 font-mono">
                  <span className="text-white/40 text-xs">$500</span>
                  <span className="text-[#E8D6A3] text-2xl font-bold" data-testid="cb-budget-value">
                    ${(+form.total_budget).toLocaleString()}
                  </span>
                  <span className="text-white/40 text-xs">$100,000</span>
                </div>
              </div>
            </div>
          )}

          {s === "creative" && (
            <div className="space-y-6">
              <SectionHead num="05" title="Creative" desc="Upload the message." />
              <DropUpload
                kind="ad_creative"
                testid="cb-upload"
                currentUrl={form.creative_url}
                onUploaded={(url, meta) =>
                  set({
                    creative_url: url,
                    creative_type: meta?.content_type?.startsWith("video/") ? "video" : "image",
                  })
                }
              />
              <div>
                <Label>Click-through URL (optional)</Label>
                <Input
                  value={form.click_url}
                  onChange={(e) => set({ click_url: e.target.value })}
                  placeholder="https://…"
                  data-testid="cb-click-url"
                  className="mt-2 bg-black border-white/10"
                />
              </div>
            </div>
          )}

          {s === "programmability" && (
            <div className="space-y-4">
              <SectionHead num="06" title="Programmable" desc="Optional. Make the ad respond to the world." />
              <RuleBuilder
                rules={form.rules}
                onChange={(rules) => set({ rules })}
                simulator
                testidPrefix="cb-rule"
                emptyHint="Skip if this is a classic campaign — you can always attach rules from the dashboard later."
              />
            </div>
          )}

          {s === "review" && (
            <div className="space-y-6">
              <SectionHead num="07" title="Review" desc="One last look before it goes live." />
              <div className="grid grid-cols-2 gap-4 font-mono text-sm">
                <ReviewKV k="Name" v={form.name} />
                <ReviewKV k="Objective" v={form.objective} />
                <ReviewKV k="I am an" v={form.advertiser_type} />
                <ReviewKV k="Brand" v={form.advertiser_company || "—"} />
                <ReviewKV k="Cities" v={selectedCities.map((c) => c.name).join(", ") || "—"} />
                <ReviewKV k="Dates" v={`${form.start_date} → ${form.end_date}`} />
                <ReviewKV k="Budget" v={`$${(+form.total_budget).toLocaleString()}`} />
                <ReviewKV k="Rules" v={form.rules.length ? `${form.rules.length} attached` : "none"} />
                <ReviewKV k="Creative" v={form.creative_type} />
              </div>
              {form.creative_url && (
                <div className="border border-white/10 max-w-sm">
                  {form.creative_type === "video" ? (
                    <video src={form.creative_url} controls className="w-full" />
                  ) : (
                    <img src={form.creative_url} alt="Creative" className="w-full" />
                  )}
                </div>
              )}
            </div>
          )}

          {s === "launch" && (
            <div className="space-y-6 text-center py-10">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3]">
                Final step
              </div>
              <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
                Send it live.
              </h2>
              <p className="text-white/50 max-w-md mx-auto">
                Your campaign will be submitted to Mirrorloops HQ for review.
                Once approved, it starts spending against selected cities and
                honouring any programmable rules you attached.
              </p>
              <Button
                onClick={launch}
                disabled={submitting}
                data-testid="cb-launch"
                className="bg-white text-black hover:bg-white/90 font-bold px-8 py-6"
              >
                {submitting ? "Submitting…" : "Submit for review"}
                <Rocket size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* INTELLIGENCE PANEL */}
        <aside className="lg:sticky lg:top-6 space-y-4" data-testid="campaign-intel">
          <div className="border border-[#E8D6A3]/20 bg-white/[0.02] p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-2 flex items-center gap-2">
              <Sparkles size={12} /> Intelligence
            </div>
            <div className="text-sm text-white/80 leading-relaxed">
              {intelligenceForStep(s, form, { avgCpm, dailyAudience })}
            </div>
            <div className="mt-4 text-[10px] text-white/40 uppercase tracking-[0.22em]">
              Reasoning · not guarantees
            </div>
          </div>

          <div className="border border-white/10 bg-white/[0.02] p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Forecast · {selectedCities.length} {selectedCities.length === 1 ? "city" : "cities"}
            </div>
            <Row k="Avg CPM" v={`$${avgCpm.toFixed(0)}`} accent />
            <Row k="Daily audience" v={dailyAudience.toLocaleString()} />
            <Row k="Est. daily impressions" v={estDailyImpressions.toLocaleString()} />
            <Row k="Est. monthly delivery" v={monthlySold.toLocaleString()} accent />
          </div>

          <div className="border border-white/10 bg-white/[0.02] p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 flex items-center gap-2">
              <Cloud size={12} /> Suggested rule
            </div>
            <div className="text-xs font-mono text-white/70">
              WHEN weather = rain{" "}
              <span className="text-[#E8D6A3]">SHOW</span> "rainy-day" creative
            </div>
            <button
              onClick={() =>
                set({
                  rules: [
                    ...form.rules,
                    {
                      id: `r_${Date.now().toString(36)}`,
                      input: "weather",
                      value: "rain",
                      action: "show",
                      creative: "Rainy-day cut",
                    },
                  ],
                })
              }
              data-testid="cb-intel-add-rule"
              className="mt-3 text-xs px-3 py-1.5 border border-[#E8D6A3]/40 text-[#E8D6A3] hover:bg-[#D4B878] hover:text-black transition"
            >
              Attach rule →
            </button>
          </div>
        </aside>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-white/10 pt-6">
        <Button
          variant="ghost"
          onClick={back}
          disabled={step === 0}
          data-testid="cb-back"
          className="text-white/60 disabled:opacity-30"
        >
          <ArrowLeft size={14} className="mr-2" /> Back
        </Button>
        {step < STEPS.length - 1 && (
          <Button
            onClick={next}
            data-testid="cb-next"
            className="bg-[#D4B878] text-black hover:bg-[#E8D6A3] font-bold px-6"
          >
            Next · {STEPS[step + 1].label} <ArrowRight size={14} className="ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

function intelligenceForStep(s, form, { avgCpm, dailyAudience }) {
  if (s === "objective") {
    return "The objective sets the whole shape of the campaign. Awareness prioritises broad reach; store visits will bias later steps toward high-footfall venues.";
  }
  if (s === "audience") {
    return "The advertiser-type also affects tone: an Artist campaign favours creative rotation and event-driven programmability; a Business campaign favours consistency and brand safety.";
  }
  if (s === "locations") {
    return `Selecting ${form.cities.length} ${form.cities.length === 1 ? "market" : "markets"} — expected daily audience ~${dailyAudience.toLocaleString()}. Consider concentrating on 2–3 cities before spreading; density beats spread for creative recall.`;
  }
  if (s === "budget") {
    const perCity = form.cities.length ? Math.round(form.total_budget / form.cities.length) : 0;
    return `At $${form.total_budget.toLocaleString()} across ${form.cities.length} ${form.cities.length === 1 ? "city" : "cities"} (~$${perCity.toLocaleString()} each), you're within a healthy CPM band around $${avgCpm.toFixed(0)}. Fewer, longer dates outperform short bursts for physical media.`;
  }
  if (s === "creative") {
    return "Physical creative is read from 3+ meters, at speed. Prefer one bold headline, one image, one call-to-action. Video should be muted-friendly — subtitle every spoken word.";
  }
  if (s === "programmability") {
    return "Adding rules doesn't restrict delivery — it makes it smarter. Weather + time rules typically lift observed relevance without changing spend.";
  }
  if (s === "review") {
    return "Everything below is what will be submitted. You can edit rules any time after launch from the Ads dashboard.";
  }
  return "Ready to send. Mirrorloops HQ reviews new campaigns within 24 hours. You will be notified.";
}

function SectionHead({ num, title, desc }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-1 font-mono">
        {num} · {title}
      </div>
      <div className="text-white/50 text-sm">{desc}</div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{children}</div>;
}

function Row({ k, v, accent }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/50">{k}</span>
      <span className={`font-mono ${accent ? "text-[#E8D6A3] font-bold" : "text-white"}`}>{v}</span>
    </div>
  );
}

function ReviewKV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{k}</div>
      <div className="text-white mt-1">{v}</div>
    </div>
  );
}
